import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, money } from "@/lib/apiClient";
import { PageHeader, StatusBadge, EmptyState } from "@/components/Common";
import { toast } from "sonner";
import { Plus, FileText, Trash2 } from "lucide-react";

const FILTERS = ["all", "draft", "sent", "viewed", "accepted", "rejected"];

export default function Quotes() {
  const [quotes, setQuotes] = useState([]);
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();

  const load = async () => setQuotes((await api.get("/quotes")).data);
  useEffect(() => { load().catch(() => {}); }, []);

  const remove = async (e, q) => {
    e.stopPropagation();
    if (!window.confirm(`Delete ${q.number}?`)) return;
    await api.delete(`/quotes/${q.id}`);
    toast.success("Document deleted");
    load();
  };

  const shown = filter === "all" ? quotes : quotes.filter((q) => q.status === filter);

  return (
    <div data-testid="quotes-page">
      <PageHeader title="Quotes & Estimates" subtitle="Track every document from draft to accepted">
        <button data-testid="new-quote-btn" onClick={() => navigate("/quotes/new")}
          className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-2 text-sm font-medium transition-colors active:scale-95">
          <Plus size={16} /> New Document
        </button>
      </PageHeader>

      <div className="flex flex-wrap gap-2 mb-5">
        {FILTERS.map((f) => (
          <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-colors border ${
              filter === f ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
            }`}>
            {f}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <EmptyState icon={FileText} title="Nothing here yet"
          description="Create a quote or estimate — GST is calculated for you."
          action={<button onClick={() => navigate("/quotes/new")} data-testid="empty-new-quote-btn"
            className="text-sm font-medium text-blue-600">Create a document →</button>} />
      ) : (
        <div className="border border-slate-200 rounded-md bg-white overflow-x-auto">
          <table className="w-full text-sm min-w-[680px]" data-testid="quotes-table">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left font-semibold px-4 py-2.5">Number</th>
                <th className="text-left font-semibold px-4 py-2.5">Type</th>
                <th className="text-left font-semibold px-4 py-2.5">Client</th>
                <th className="text-left font-semibold px-4 py-2.5">Title</th>
                <th className="text-left font-semibold px-4 py-2.5">Status</th>
                <th className="text-right font-semibold px-4 py-2.5">GST</th>
                <th className="text-right font-semibold px-4 py-2.5">Total</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {shown.map((q) => (
                <tr key={q.id} data-testid={`quote-row-${q.number}`} onClick={() => navigate(`/quotes/${q.id}`)}
                  className="border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors cursor-pointer">
                  <td className="px-4 py-3 font-semibold text-slate-900">{q.number}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded ${
                      q.doc_type === "quote" ? "bg-blue-50 text-blue-700" : "bg-slate-100 text-slate-600"}`}>
                      {q.doc_type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{q.client_name || "—"}</td>
                  <td className="px-4 py-3 text-slate-600 max-w-[220px] truncate">{q.title || "—"}</td>
                  <td className="px-4 py-3"><StatusBadge status={q.status} /></td>
                  <td className="px-4 py-3 text-right text-slate-500">{money(q.totals.gst_amount)}</td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-900">{money(q.totals.total_inc_gst)}</td>
                  <td className="px-4 py-3 text-right">
                    <button data-testid={`delete-quote-${q.number}`} onClick={(e) => remove(e, q)} aria-label="Delete document"
                      className="p-1.5 rounded-md text-slate-400 hover:bg-red-50 hover:text-red-600 transition-colors">
                      <Trash2 size={15} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
